class CustomSearchBar extends HTMLElement {
    constructor() {
        super();

        this.overlay = this.querySelector('#custom-search-overlay');
        this.backdrop = this.querySelector('#custom-search-backdrop');
        this.searchInput = this.querySelector('#custom-search-input');
        this.closeBtn = this.querySelector('#custom-search-close');
        this.form = this.querySelector('#custom-search-form');
        this.submitBtn = this.querySelector('#custom-search-submit-btn');
        this.clearBtn = this.querySelector('#custom-search-clear-btn');

        // Default & results containers
        this.defaultBody = this.querySelector('#custom-search-default');
        this.resultsBody = this.querySelector('#custom-search-results');
        this.suggestionsContainer = this.querySelector('#custom-search-suggestions-list');
        this.productsContainer = this.querySelector('#custom-search-products-grid');
        this.suggestedProductsGrid = this.querySelector('#custom-search-suggested-grid');
        this.loadingIndicator = this.querySelector('#custom-search-loading');

        // Use querySelectorAll for all trigger buttons (there may be multiple in header)
        this.triggerButtons = document.querySelectorAll('.custom-search-trigger');

        // Debounce timer
        this.debounceTimer = null;
        this.abortController = null;

        this.openSearch = this.openSearch.bind(this);
        this.closeSearch = this.closeSearch.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onClearClick = this.onClearClick.bind(this);
        this.animationStarted = false;
    }

    connectedCallback() {
        this.triggerButtons.forEach((btn) => {
            btn.addEventListener('click', this.openSearch);
        });

        this.closeBtn.addEventListener('click', this.closeSearch);
        this.backdrop.addEventListener('click', this.closeSearch);
        document.addEventListener('keydown', this.handleKeydown);
        this.searchInput.addEventListener('input', this.onInput);
        this.form.addEventListener('submit', this.onFormSubmit);
        this.submitBtn.addEventListener('click', this.onSubmitClick);
        this.clearBtn.addEventListener('click', this.onClearClick);

        // Fetch recommended products from collection on page load
        this.fetchRecommendedProducts();
        this.startPlaceholderAnimation();
    }

    disconnectedCallback() {
        this.triggerButtons.forEach((btn) => {
            btn.removeEventListener('click', this.openSearch);
        });

        this.closeBtn.removeEventListener('click', this.closeSearch);
        this.backdrop.removeEventListener('click', this.closeSearch);
        document.removeEventListener('keydown', this.handleKeydown);
        this.searchInput.removeEventListener('input', this.onInput);
        this.form.removeEventListener('submit', this.onFormSubmit);
        this.submitBtn.removeEventListener('click', this.onSubmitClick);
        this.clearBtn.removeEventListener('click', this.onClearClick);
    }

    openSearch() {
        this.overlay.classList.add('is-open');
        this.backdrop.classList.add('is-visible');
        document.body.classList.add('search-overlay-open');

        setTimeout(() => {
            this.searchInput.focus();
        }, 400);
    }

    closeSearch() {
        this.overlay.classList.add('is-closing');
        this.overlay.classList.remove('is-open');
        this.backdrop.classList.remove('is-visible');
        document.body.classList.remove('search-overlay-open');
        this.searchInput.value = '';
        this.clearBtn.style.display = 'none';

        // Reset to default view
        this.showDefaultBody();

        this.overlay.addEventListener('transitionend', () => {
            this.overlay.classList.remove('is-closing');
        }, { once: true });
    }

    handleKeydown(e) {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-open')) {
            this.closeSearch();
        }
    }

    /* ==============================
       Form & Submit
       ============================== */
    onFormSubmit(e) {
        if (!this.searchInput.value.trim().length) {
            e.preventDefault();
        }
    }

    onSubmitClick() {
        if (this.searchInput.value.trim().length) {
            this.form.submit();
        }
    }

    onClearClick() {
        this.searchInput.value = '';
        this.clearBtn.style.display = 'none';
        this.searchInput.focus();
        this.showDefaultBody();

        // Abort any pending fetch
        if (this.abortController) {
            this.abortController.abort();
        }
        clearTimeout(this.debounceTimer);
    }

    /* ==============================
       Predictive Search
       ============================== */
    onInput() {
        const query = this.searchInput.value.trim();

        clearTimeout(this.debounceTimer);

        if (!query.length) {
            this.clearBtn.style.display = 'none';
            this.showDefaultBody();
            return;
        }

        this.clearBtn.style.display = '';

        this.debounceTimer = setTimeout(() => {
            this.fetchResults(query);
        }, 300);
    }

    fetchResults(query) {
        // Abort any in-flight request
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        this.showLoading();

        const url = `${theme.routes.predictive_search_url}?q=${encodeURIComponent(query)}&${encodeURIComponent('resources[type]')}=product,query&${encodeURIComponent('resources[limit]')}=10&section_id=predictive-search`;

        fetch(url, { signal: this.abortController.signal })
            .then((response) => {
                if (!response.ok) throw new Error(response.status);
                return response.text();
            })
            .then((text) => {
                const html = new DOMParser().parseFromString(text, 'text/html');
                const section = html.querySelector('#shopify-section-predictive-search');

                if (!section) {
                    this.showDefaultBody();
                    return;
                }

                this.renderResults(section);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Predictive search error:', error);
                    this.showDefaultBody();
                }
            });
    }

    renderResults(section) {
        // Extract suggestions (queries)
        const queryLinks = section.querySelectorAll('.thb-predictive-search--popular a');
        // Extract products
        const productCards = section.querySelectorAll('.side-panel-content--tab-panel.tab-active .product-cart-item');

        // Build suggestions HTML
        let suggestionsHTML = '';
        queryLinks.forEach((link) => {
            suggestionsHTML += `<a href="${link.getAttribute('href')}" class="custom-search-suggestion-link">${link.innerHTML}</a>`;
        });

        // Build products HTML
        let productsHTML = '';
        productCards.forEach((card) => {
            const link = card.querySelector('a');
            const img = card.querySelector('img');
            const title = card.querySelector('.cart-product-link');

            if (!link) return;

            const href = link.getAttribute('href') || '#';
            let imgSrc = '';
            let imgAlt = '';            
            if (img) {
                imgSrc = img.getAttribute('src') || '';
                // If no src, try to extract first URL from srcset
                if (!imgSrc && img.getAttribute('srcset')) {
                    imgSrc = img.getAttribute('srcset').split(',')[0].trim().split(' ')[0];
                }
                // Request a larger image from Shopify CDN for crisp rendering
                if (imgSrc) {
                    // Old CDN format: _20x_crop_center.jpg → _400x_crop_center.jpg
                    imgSrc = imgSrc.replace(/_\d+x_crop_center/, '_400x_crop_center');
                    // New CDN format: ?width=192 → ?width=400
                    imgSrc = imgSrc.replace(/width=\d+/, 'width=400');
                }
                imgAlt = img.getAttribute('alt') || '';
            }
            const titleText = title ? title.textContent.trim() : '';

            // Extract just the raw price values (not the full snippet markup)
            const sellingPriceEl = card.querySelector('ins .amount, .money-price');
            const compareAtPriceEl = card.querySelector('del .amount');
            const sellingPrice = sellingPriceEl ? sellingPriceEl.textContent.trim() : '';
            const compareAtPrice = compareAtPriceEl ? compareAtPriceEl.textContent.trim() : '';

            productsHTML += `
        <a href="${href}" class="custom-search-product-card">
          <div class="custom-search-product-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${imgAlt}" loading="eager">` : '<div class="custom-search-product-placeholder"></div>'}
          </div>
          <div class="custom-search-product-info">
            <span class="custom-search-product-title">${titleText}</span>
            <span class="custom-search-product-price">
              <span class="custom-search-selling-price">${sellingPrice}</span>
              ${compareAtPrice ? `<span class="custom-search-compare-price"><s>${compareAtPrice}</s></span>` : ''}
            </span>
          </div>
        </a>
      `;
        });

        // If no results at all, show "no results"
        if (!suggestionsHTML && !productsHTML) {
            this.suggestionsContainer.innerHTML = '<p class="custom-search-no-results">No results found</p>';
            this.productsContainer.innerHTML = '';
        } else {
            this.suggestionsContainer.innerHTML = suggestionsHTML || '<p class="custom-search-no-results">No suggestions</p>';
            this.productsContainer.innerHTML = productsHTML || '<p class="custom-search-no-results">No products found</p>';
        }

        this.showResultsBody();
    }

    /* ==============================
       Recommended Products (Collection Fetch)
       ============================== */
    fetchRecommendedProducts() {
        fetch('/collections/26p-search-bar-recommendations-collection/products.json?limit=10')
            .then((res) => {
                if (!res.ok) throw new Error(res.status);
                return res.json();
            })
            .then((data) => {
                const products = data.products;
                if (products && products.length) {
                    this.renderSuggestedProducts(products);
                }
            })
            .catch((err) => {
                console.error('Recommended products fetch error:', err);
                // Fallback: keep the customizer products (server-rendered), do nothing
            });
    }

    renderSuggestedProducts(products) {
        let html = '';
        products.forEach((product) => {
            const url = `/products/${product.handle}`;
            const imgSrc = product.images?.length ? this.getResizedImageUrl(product.images[0].src, 400) : '';
            const imgAlt = product.images?.[0]?.alt || product.title;
            const title = product.title;
            const price = parseFloat(product.variants[0]?.price || 0);
            const compareAtPrice = parseFloat(product.variants[0]?.compare_at_price || 0);
            const formattedPrice = this.formatMoney(price);
            const formattedComparePrice = this.formatMoney(compareAtPrice);

            html += `
                <a href="${url}" class="custom-search-product-card">
                    <div class="custom-search-product-image">
                        ${imgSrc ? `<img src="${imgSrc}" alt="${this.escapeHtml(imgAlt)}" loading="lazy">` : '<div class="custom-search-product-placeholder"></div>'}
                    </div>
                    <div class="custom-search-product-info">
                        <span class="custom-search-product-title">${this.escapeHtml(title)}</span>
                        <span class="custom-search-product-price">
                            <span class="custom-search-selling-price">${formattedPrice}</span>
                            ${compareAtPrice > price ? `<span class="custom-search-compare-price"><s>${formattedComparePrice}</s></span>` : ''}
                        </span>
                    </div>
                </a>
            `;
        });

        this.suggestedProductsGrid.innerHTML = html;
    }

    formatMoney(amount) {
        if (isNaN(amount)) return '';
        try {
            const currency = window.Shopify?.currency?.active || 'INR';
            return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
        } catch {
            return `Rs. ${amount.toFixed(2)}`;
        }
    }

    getResizedImageUrl(url, width) {
        if (!url) return '';
        try {
            const urlObj = new URL(url);
            urlObj.searchParams.set('width', width);
            return urlObj.toString();
        } catch {
            return url;
        }
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    /* ==============================
       Show / Hide helpers
       ============================== */
    showDefaultBody() {
        this.defaultBody.style.display = '';
        this.resultsBody.style.display = 'none';
        this.loadingIndicator.style.display = 'none';
    }

    showResultsBody() {
        this.defaultBody.style.display = 'none';
        this.resultsBody.style.display = '';
        this.loadingIndicator.style.display = 'none';
    }

    showLoading() {
        this.defaultBody.style.display = 'none';
        this.resultsBody.style.display = 'none';
        this.loadingIndicator.style.display = '';
    }
    startPlaceholderAnimation() {

    if (this.animationStarted) return;
    this.animationStarted = true;

    const input = this.searchInput;

    if (!input) return;

    const products = (input.dataset.searchTerms || "")
        .replace(/^"+|"+$/g, "")
        .split(",")
        .map(item => item.trim())
        .filter(Boolean);

    if (!products.length) return;

    let productIndex = 0;
    let charIndex = 0;
    let deleting = false;

    const animate = () => {

        // Stop animation while customer is typing
        if (
            document.activeElement === input &&
            input.value.length > 0
        ) {
            setTimeout(animate, 500);
            return;
        }

        const currentWord = products[productIndex];

        if (!deleting) {
            charIndex++;

            input.placeholder =
                `SEARCH ${currentWord.substring(0, charIndex)}`;

            if (charIndex >= currentWord.length) {
                deleting = true;
                setTimeout(animate, 1500);
                return;
            }
        } else {
            charIndex--;

            input.placeholder =
                `SEARCH ${currentWord.substring(0, charIndex)}`;

            if (charIndex <= 0) {
                deleting = false;
                productIndex =
                    (productIndex + 1) % products.length;
            }
        }

        setTimeout(
            animate,
            deleting ? 60 : 100
        );
    };

    animate();
    }
}

customElements.define('custom-search-bar', CustomSearchBar);
